/**
 * Proposal share links — a read-only view of one payment proposal that a
 * member sends to the payee (no account needed).
 *
 * Mirrors ssp-relay-enterprise/src/types/proposalShare.ts. The public view is
 * built server-side field by field; everything in it is still treated as
 * untrusted text on the public page.
 */

import type { ProposalRecipient, ProposalStatus } from './vault';

/** Link lifetimes the relay accepts (days). */
export const SHARE_EXPIRY_DAYS = [1, 7, 30, 90] as const;
export type ShareExpiryDays = (typeof SHARE_EXPIRY_DAYS)[number];
export const DEFAULT_SHARE_EXPIRY_DAYS: ShareExpiryDays = 30;
export const MAX_SHARE_LABEL_LENGTH = 80;
/** Active (unexpired, unrevoked) links allowed per proposal. */
export const MAX_ACTIVE_SHARES_PER_PROPOSAL = 20;

/** What the creator chose to reveal beyond the payment itself. */
export interface ProposalShareOptions {
  /** Display names of the members who signed (otherwise "Signature 1 of 3"). */
  showSignerNames: boolean;
  /** The proposal's memo / note. */
  showMemo: boolean;
  /** The unsigned transaction / UserOperation being signed. */
  showTechnical: boolean;
}

export type ProposalShareStatus = 'active' | 'expired' | 'revoked';

/** A share link as members see it. */
export interface ProposalShare {
  id: string;
  proposalId: string;
  vaultId: string;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  expiresAt: string;
  label?: string;
  options: ProposalShareOptions;
  status: ProposalShareStatus;
  revokedAt?: string;
  /** wkIdentity of the member who revoked it (absent when SSP revoked it). */
  revokedBy?: string;
  revokedByName?: string;
  /** Revoked by SSP support from the admin dashboard. */
  revokedByAdmin?: boolean;
  viewCount: number;
  firstViewedAt?: string;
  lastViewedAt?: string;
  /**
   * The secret for the link (`/share#<token>`). Present on create, and on
   * list while the link is active and the server can decrypt its copy.
   */
  token?: string;
}

export interface CreateProposalShareRequest {
  /** Default 30. */
  expiresInDays?: ShareExpiryDays;
  label?: string;
  showSignerNames?: boolean;
  showMemo?: boolean;
  showTechnical?: boolean;
}

export interface ProposalShareResponse {
  success: boolean;
  share?: ProposalShare;
  shares?: ProposalShare[];
  /** False when the organization has turned share links off. */
  sharingEnabled?: boolean;
  error?: string;
  errorCode?: string;
}

/**
 * Receiver-facing progress, derived from the proposal status:
 * - awaiting_approval: an admin / approval workflow must approve first
 * - collecting_signatures: signers are signing
 * - scheduled: fully signed, held by a time lock until `timeLock.scheduledBroadcastAt`
 * - broadcasting: fully signed, being submitted
 * - sent: on the network, waiting for confirmation
 * - confirmed / failed / rejected / cancelled / expired: final
 */
export type SharedProposalStage =
  | 'awaiting_approval'
  | 'collecting_signatures'
  | 'scheduled'
  | 'broadcasting'
  | 'sent'
  | 'confirmed'
  | 'failed'
  | 'rejected'
  | 'cancelled'
  | 'expired';

export const SHARED_PROPOSAL_STAGES: readonly SharedProposalStage[] = [
  'awaiting_approval',
  'collecting_signatures',
  'scheduled',
  'broadcasting',
  'sent',
  'confirmed',
  'failed',
  'rejected',
  'cancelled',
  'expired',
];

export type SharedTimelineEvent =
  | 'created'
  | 'approval_required'
  | 'approved'
  | 'approval_rejected'
  | 'signed'
  | 'scheduled'
  | 'broadcast'
  | 'confirmed'
  | 'reverted'
  | 'failed'
  | 'rejected'
  | 'cancelled'
  | 'expired';

export interface SharedTimelineEntry {
  event: SharedTimelineEvent;
  at: string;
  /** In the future (a scheduled broadcast). */
  upcoming?: boolean;
  /** n-th signature / approval round … */
  index?: number;
  /** … of this many. */
  of?: number;
  /** Signer display name (only with showSignerNames). */
  name?: string;
}

/** The unsigned payload being signed (only with showTechnical). */
export type SharedSigningPayload =
  | {
      kind: 'utxo';
      /** Unsigned transaction hex; the outputs are what gets paid. */
      rawUnsignedTx?: string;
      /** Value of each input in the chain's smallest unit. */
      inputAmounts: string[];
    }
  | {
      kind: 'evm';
      sender: string;
      nonce: string;
      callData: string;
      callGasLimit: string;
      verificationGasLimit: string;
      preVerificationGas: string;
      maxFeePerGas: string;
      maxPriorityFeePerGas: string;
      /** The operation also deploys the vault's smart account. */
      deploysAccount: boolean;
    }
  | {
      kind: 'solana';
      flow: 'bundled' | 'split';
    };

export type SharedApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface SharedProposalView {
  link: { expiresAt: string; label?: string };
  organizationName: string;
  /** Proposal id — a reference both sides can quote. */
  reference: string;
  chain: string;
  status: ProposalStatus;
  stage: SharedProposalStage;
  createdAt: string;
  updatedAt: string;
  /** When an unsigned proposal lapses. */
  expiresAt: string;
  fromAddress?: string;
  recipients: ProposalRecipient[];
  tokenContract?: string;
  tokenSymbol?: string;
  tokenDecimals?: number;
  fee?: string;
  /** USD value of the payment when it was created. */
  amountUsd?: number;
  memo?: string;
  signatures: {
    required: number;
    collected: number;
  };
  approval?: {
    status: SharedApprovalStatus;
    rounds?: Array<{
      required: number;
      approved: number;
      status: 'pending' | 'approved' | 'rejected' | 'timed_out';
    }>;
  };
  timeLock?: { scheduledBroadcastAt: string; cancelled: boolean };
  /** Transaction hash to look up on a block explorer, once broadcast. */
  txid?: string;
  /** EVM: the UserOperation hash (txid is the bundle transaction). */
  userOpHash?: string;
  broadcastAt?: string;
  confirmedAt?: string;
  /** EVM: false when the operation landed but its execution reverted. */
  executionSuccess?: boolean;
  timeline: SharedTimelineEntry[];
  signing?: SharedSigningPayload;
  /** Suggested seconds until the next refresh; absent once final. */
  refreshAfterSec?: number;
}

export interface SharedProposalViewResponse {
  success: boolean;
  view?: SharedProposalView;
  error?: string;
  /** SHARE_NOT_FOUND | SHARE_EXPIRED | SHARE_REVOKED | SHARE_DISABLED */
  errorCode?: string;
}

/**
 * Why the public page could not show a payment. The first four come from the
 * relay's errorCode; the rest are transport-level.
 */
export type SharedProposalErrorReason =
  | 'not_found'
  | 'expired'
  | 'revoked'
  | 'disabled'
  | 'rate_limited'
  | 'network'
  | 'unavailable';

export type SharedProposalResult =
  | { ok: true; view: SharedProposalView }
  | { ok: false; reason: SharedProposalErrorReason };
